import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  catchError,
  map,
  switchMap,
  take, of,
} from 'rxjs';
import {
  Account,
  AccountListItem,
  AccountListResponse,
  AccountResponse,
  AccountUser,
  AccountUserListItem,
  AccountUserListResponse,
  AccountUserLookups,
  AccountUserLookupsResponse,
  AccountUserPasswordObject,
  AccountUserResponse,
  AccountUserRoleItem,
} from './accounts.types';
import { CathyService } from '@sbd-account-portal/app/services/cathy.service';
import { environment } from '@sbd-account-portal/app/environments/environment';
import { State, StatesLookupResponse } from '@sbd-doug-frontend/shared/lib/types/ui-lookups/states.types';
import {
  AssigneeItem,
} from '@sbd-doug-frontend/app/modules/accounts/accounts-manager/acccount-user-list/account-user-vtabs/account-user-tasks/tasksets-list/tasksets.types';

@Injectable({
  providedIn: 'root',
})
export class AccountUsersService {
  accountStatuses = signal<[]>([]);
  statesLookup = signal<State[]>([]);
  accountSummaryDaysOptions = signal(null);
  preferenceFormIsLoading = signal(true);
  uiLookups = signal<AccountUserLookups>(null);
  selectedAccountUser = signal<AccountUser>(null);
  defaultTaskAssignees = signal<AssigneeItem[]>([]);

  private _statesLookup: BehaviorSubject<State[]> = new BehaviorSubject<State[]>([]);

  private defaultViewPermission: string[] = ['accountuserviewall'];
  private _account: BehaviorSubject<Account | null> = new BehaviorSubject(null);
  private _accountList: BehaviorSubject<AccountListItem[] | null> =
    new BehaviorSubject(null);
  private _accountUser: BehaviorSubject<AccountUser | null> =
    new BehaviorSubject(null);
  private _accountUserList: BehaviorSubject<AccountUserListItem[] | null> =
    new BehaviorSubject(null);
  private _totalAccountUsers: BehaviorSubject<number> = new BehaviorSubject(0);
  private _accountUserDetailsPagePermission: BehaviorSubject<{ op: string, permissions: string[] } | null> = new BehaviorSubject(null);
  _accountUserLookups: BehaviorSubject<AccountUserLookups | null> = new BehaviorSubject(null);
  _accountUserRoles: BehaviorSubject<AccountUserRoleItem[] | null> = new BehaviorSubject(null);

  private _sessionStorage: Storage;
  private _storageKey = 'sharedServiceCache-system-account-user-ui-lookups';

  constructor(
    private _httpClient: HttpClient,
    private _cathyService: CathyService,
  ) {
    this._accountUserDetailsPagePermission.next({ op: 'AND', permissions: this.defaultViewPermission });
    // this.getAccountUsersUILookups().subscribe()
  }

  get account$(): Observable<Account> {
    return this._account.asObservable();
  }

  get accountUser$(): Observable<AccountUser> {
    return this._accountUser.asObservable();
  }

  get accountUserDetailsPagePermission$(): Observable<{ op: string, permissions: string[] }> {
    return this._accountUserDetailsPagePermission.asObservable();
  }

  accountUser(accountUser: AccountUser) {
    this._accountUser.next(accountUser);
  }

  addPermission(permission = '', logicalOp = '') {
    this.defaultViewPermission.push(permission);
    this._accountUserDetailsPagePermission.next({ op: logicalOp, permissions: this.defaultViewPermission });
  }

  get totalAccountUsers$(): Observable<number> {
    return this._totalAccountUsers.asObservable();
  }

  get accountList$(): Observable<AccountListItem[]> {
    return this._accountList.asObservable();
  }

  get accountUserList$(): Observable<AccountUserListItem[]> {
    return this._accountUserList.asObservable();
  }

  get accountUserLookups$(): Observable<AccountUserLookups> {
    return this._accountUserLookups.asObservable();
  }

  get accountUserRoles$(): Observable<AccountUserRoleItem[]> {
    return this._accountUserRoles.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  //ACCOUNT METHODS

  updateAccount(account: Account): Observable<Account> {
    return this.accountList$.pipe(
      take(1),
      switchMap((currentAccount) =>
        this._httpClient
          .put<AccountResponse>(
            `${environment.apiLocalUrl}/api/v4/account/${account.account_id}`,
            account,
          )
          .pipe(
            map((updatedAccount) => {
              if (currentAccount) {
                const index = currentAccount.findIndex(
                  (item) => item.account_id === account.account_id,
                );

                currentAccount[index] = updatedAccount.data.account.account;
              }
              updatedAccount.data.account.account.agency_default_commission = updatedAccount.data.account.account.agency_default_commission * 100;
              this._accountList.next(currentAccount);
              this._account.next(updatedAccount.data.account.account);
              return updatedAccount.data.account.account;
            }),
          ),
      ),
    );
  }

  create(account: Account): Observable<Account> {
    return this.accountList$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .post<AccountResponse>(
            `${environment.apiLocalUrl}/api/v4/account`,
            account,
          )
          .pipe(
            map((updatedAccount) => {
              if (updatedAccount.success == false) {
                throw new Error(updatedAccount.message);
              } else {
                this._account.next(updatedAccount.data.account.account);
                return updatedAccount.data.account.account;
              }
            }),
          ),
      ),
    );
  }

  //ACCOUNT USER METHODS
  getAccountUsers(filters: any = {}): Observable<AccountUserListItem[]> {
    const params = filters || {};

    return this._httpClient
      .get<AccountUserListResponse>(
        `${environment.apiLocalUrl}/accounts/users`,
        {
          params: params,
        },
      )
      .pipe(
        map((response) => {
          this._accountUserList.next(response.data.account_users.account_users);
          this._totalAccountUsers.next(
            response.data.account_users.total_account_users,
          );
          return response.data.account_users.account_users;
        }),
      );
  }

  getAccountStatusesLookup(): Observable<any[]> {
    return this._httpClient.get<any>(
      `${environment.apiLocalUrl}/accounts/statuses`,
    ).pipe(
      map(response => {
        const statuses = response.data.statuses.statuses;
        return statuses;
      }),
    );
  }

  getAccountUser(accountUserId): Observable<AccountUser> {
    return this._httpClient
      .get<AccountUserResponse>(
        `${environment.apiLocalUrl}/accounts/user/${accountUserId}`,
      )
      .pipe(
        map((response: AccountUserResponse) => {
          if (response.success === false) {
            this._accountUser.next(null);
            return null;
          } else {
            this._accountUser.next(response.data.account_user.account_user);
            this.selectedAccountUser.set(response.data.account_user.account_user);
            return response.data.account_user.account_user;
          }
        }),
        catchError((err, caught) => {
          return EMPTY;
        }),
      );
  }

  clearAccountUser() {
    this._accountUser.next(null);
    this.selectedAccountUser.set(null);
  }

  changeAccountUserPassword(
    pass: AccountUserPasswordObject,
    accountUserId: number,
  ): Observable<boolean> {
    return this._accountUser.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .put<AccountUserResponse>(
            `${environment.apiLocalUrl}/accounts/user/${accountUserId}/change-password`,
            pass,
          )
          .pipe(
            map((updatedAccountUser) => {
              if (updatedAccountUser.success === false) {
                throw new Error(updatedAccountUser.message);
              } else {
                // this._accountUser.next(
                //   updatedAccountUser.data.account_user.account_user
                // );
                // return updatedAccountUser.data.account_user.account_user;
                return true;
              }
            }),
          ),
      ),
    );
  }

  updateAccountUser(
    user: AccountUser,
    accountUserId: number,
  ): Observable<AccountUser> {
    return this.accountUserList$.pipe(
      take(1),
      switchMap((currentAccountUser) =>
        this._httpClient
          .put<AccountUserResponse>(
            `${environment.apiLocalUrl}/accounts/user/${accountUserId}`,
            user,
          )
          .pipe(
            map((updatedAccountUser) => {
              if (updatedAccountUser.success == false) {
                throw new Error(updatedAccountUser.message);
              } else {
                this._accountUserList.next(currentAccountUser);
                this._accountUser.next(
                  updatedAccountUser.data.account_user.account_user,
                );
                return updatedAccountUser.data.account_user.account_user;
              }
            }),
          ),
      ),
    );
  }

  createAccountUser(user: AccountUser): Observable<AccountUser> {
    return this.accountUserList$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .post<AccountUserResponse>(
            `${environment.apiLocalUrl}/accounts/user`,
            user,
          )
          .pipe(
            map((updatedAccountUser) => {
              if (updatedAccountUser.success == false) {
                throw new Error(updatedAccountUser.message);
              } else {
                //No need as route redirection on created ID is happening /
                //We need to avoid double event, route change and selectedaccountuser change resulting on api call /getAccountUser happening twice
              /*  this._accountUser.next(
                  updatedAccountUser.data.account_user.account_user,
                );*/
                return updatedAccountUser.data.account_user.account_user;
              }
            }),
          ),
      ),
    );
  }

  getStatesCache(): State[] {
    const storageItem = this._sessionStorage.getItem(this._storageKey);
    return storageItem ? <State[]>JSON.parse(storageItem) : [];
  }

  getStatesLookup(): Observable<State[]> {
    const states = this.getStatesCache();

    if (states.length > 0) {
      this.statesLookup.set(states);
      this._statesLookup.next(states);
      return of(states);
    }

    return this._httpClient
      .get<StatesLookupResponse>(`${environment.apiLocalUrl}/accounts/ui-lookups/states`)
      .pipe(
        map((response) => {
          this.statesLookup.set(response.data.lookup.states);
          this._statesLookup.next(response.data.lookup.states);
          this.saveStatesCache(response.data.lookup.states);
          return response.data.lookup.states;
        }),
      );
  }

  saveStatesCache(states: State[]) {
    const jsonData = JSON.stringify(states);
    this._sessionStorage.setItem(this._storageKey, jsonData);
  }

}
